import React from 'react';
import NextImage from 'next/legacy/image';
import { isMobile } from 'react-device-detect';
import classnames from 'classnames';

import type { SliderType } from '@components/ui';
import {
  Image, Slide, Slider
} from '@components/ui';
import { IMAGES } from '@constants/images';
import { isArrayEmpty } from '@helpers/ArrayHelpers';

import ProductGalleryThumbnails from './ProductGalleryThumbnails';
import ProductGalleryZoom from './ProductGalleryZoom';

import type { IProductImage } from 'types';

import styles from './ProductGallery.module.scss';

const IMAGE_SIZE = 590;
const CLICK_DETECTION_DELTA = 5;

const ZOOM_LEVELS = [
  { minRatio: 1, maxRatio: 1 },
  { minRatio: 3, maxRatio: 3 },
  { minRatio: 7, maxRatio: 7 },
];

interface Props {
  className?: string,
  //
  productName: string,
  images: IProductImage[],
  //
  canZoom?: boolean,
  isAvailable?: boolean,
  isQuickView?: boolean,
  isImageMaximized?: boolean,
  shouldOffsetBullets?: boolean,
  //
  setImageMaximized: (isMaximized: boolean) => void,
  //
  children: React.ReactNode
}

interface CursorPosition {
  startX: number,
  startY: number
}

const ProductGallery = (props: Props) => {
  const {
    className,
    //
    productName,
    images = [],
    //
    canZoom,
    isAvailable,
    isQuickView,
    isImageMaximized,
    shouldOffsetBullets,
    //
    setImageMaximized = () => {},
    //
    children,
  } = props;

  // Refs

  const cursorPosition = React.useRef<CursorPosition>();
  const [swiperRef, setSwiperRef] = React.useState<SliderType>(null);

  // Hooks

  const [isDragging, setIsDragging] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [zoomLevelIndex, setZoomLevelIndex] = React.useState(0);

  // Empty state

  if (isArrayEmpty(images, true)) {
    return (
      <div
        className={classnames(
          styles.root,
          styles.empty,
          className
        )}
      >
        <div className={classnames(styles.imageWrapper, styles.empty)}>
          <Image
            className={styles.image}
            next={false}
            src={null}
          />
        </div>
      </div>
    );
  }

  // Handlers

  const handleZoomToggle = () => {
    if (isImageMaximized && swiperRef) {
      const { zoom } = swiperRef;
      const { out: zoomOut = () => {} } = zoom || {};

      setZoomRatio(0);
      setZoomLevelIndex(0);
      zoomOut();
    }

    setImageMaximized(!isImageMaximized);
  };

  const handleZoomIn = () => {
    if (canZoomIn) {
      const { zoom } = swiperRef;
      const { in: zoomIn = () => {} } = zoom || {};
      setZoomRatio(zoomLevelIndex + 1);
      setZoomLevelIndex((prev) => prev + 1);
      zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (canZoomOut) {
      const { zoom } = swiperRef;
      const { out: zoomOut = () => {} } = zoom || {};
      setZoomRatio(0);
      setZoomLevelIndex(0);
      zoomOut();
    }
  };

  const setZoomRatio = (index: number) => {
    if (swiperRef) {
      const {
        minRatio,
        maxRatio
      } = ZOOM_LEVELS[index];

      (swiperRef.params.zoom as any).minRatio = minRatio;
      (swiperRef.params.zoom as any).maxRatio = maxRatio;
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (cursorPosition.current) {
      const { startX, startY } = cursorPosition.current;
      const diffX = Math.abs(e.pageX - startX);
      const diffY = Math.abs(e.pageY - startY);

      if (diffX < CLICK_DETECTION_DELTA && diffY < CLICK_DETECTION_DELTA) {
        if (canZoomIn) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
      setIsDragging(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    cursorPosition.current = {
      startX: e.pageX,
      startY: e.pageY
    };

    setIsDragging(true);
  };

  const handleClick = () => {
    setImageMaximized(true);
  };

  const canZoomIn = zoomLevelIndex < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomLevelIndex > 0;

  const slideClasses = classnames(
    styles.slide,
    { [styles.isImageMaximized]: isImageMaximized },
    { [styles.zoomIn]: isImageMaximized && canZoomIn },
    { [styles.zoomOut]: isImageMaximized && (!canZoomIn && canZoomOut) },
    { [styles.dragging]: isImageMaximized && isDragging }
  );

  return (
    <>
      <div
        className={classnames(
          styles.root,
          className
        )}
      >

        {/* Product Images */}
        <Slider
          className={classnames(
            styles.carousel,
            { [styles.maximized]: isImageMaximized }
          )}
          prevClassName={styles.carouselPrev}
          nextClassName={styles.carouselNext}
          //
          slidesPerView={1}
          allowTouchMove={isMobile}
          preventClicks={isMobile}
          preventClicksPropagation={isMobile}
          noSwiping={!isMobile}
          //
          navigation
          zoom={ZOOM_LEVELS[0]}
          bullets={
            isImageMaximized
              ? (isMobile ? { position: 'center' } : undefined)
              : (isMobile ? { position: 'center', offsetY: shouldOffsetBullets } : undefined)
          }
          //
          onSwiper={(e) => setSwiperRef(e)}
          onSlideChange={() => setZoomLevelIndex(0)}
          onRealIndexChange={(swiper) => setActiveIndex(swiper?.realIndex)}
        >
          {
            images?.map((image, index) => (
              <Slide
                key={index}
                className={slideClasses}
                zoom
                onMouseUp={(!isMobile && isImageMaximized) ? handleMouseUp : undefined}
                onMouseDown={(!isMobile && isImageMaximized) ? handleMouseDown : undefined}
                onClick={(isMobile || isImageMaximized) ? undefined : handleClick}
              >
                <ProductImage
                  key={index}
                  name={productName}
                  image={image}
                  isAvailable={isAvailable}
                  isQuickView={isQuickView}
                  isImageMaximized={isImageMaximized}
                />
              </Slide>

            ))
          }
        </Slider>

        {/* Zoom controls */}
        <div className={styles.zoomControls}>
          {
            (canZoom || isImageMaximized) && (
              <ProductGalleryZoom
                isImageMaximized={isImageMaximized}
                canZoomIn={canZoomIn}
                canZoomOut={canZoomOut}
                //
                onZoomToggle={handleZoomToggle}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
              />
            )
          }
        </div>

        {/* Children */}
        <div className={styles.children}>
          {children}
        </div>

      </div>

      {/* Image thumbnails */}
      <ProductGalleryThumbnails
        className={styles.thumbnails}
        productName={productName}
        images={images}
        swiperRef={swiperRef}
        activeIndex={activeIndex}
      />
    </>
  );
};

// --- Image

interface ProductImageProps {
  isAvailable: boolean,
  isImageMaximized: boolean,
  isQuickView: boolean,
  //
  image: IProductImage,
  name: string
}

const ProductImage = (props: ProductImageProps) => {
  const {
    isAvailable,
    isImageMaximized,
    isQuickView,
    //
    image,
    name,
  } = props;

  // Props

  const src = getImage(image, isImageMaximized);

  const imageClasses = classnames(
    styles.image,
    { [styles.unavailable]: !isAvailable }
  );

  if (isImageMaximized) {
    return (
      <img
        className={imageClasses}
        width={IMAGE_SIZE}
        height={IMAGE_SIZE}
        src={src}
        alt={name}
      />
    );
  }

  return (
    <div
      className={classnames(
        styles.imageWrapper,
        { 'swiper-zoom-container': isMobile }
      )}
    >
      <div
        className={classnames({ 'swiper-zoom-target': isMobile })}
      >
        <NextImage
          className={classnames(
            styles.image,
            { [styles.unavailable]: !isAvailable }
          )}
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          src={src}
          alt={name}
          unoptimized={isQuickView}
          priority={!isQuickView}
          quality={100}
        />
      </div>
    </div>
  );
};

// Export

export default ProductGallery;

// Helpers

const getImage = (image: IProductImage, isImageMaximized: boolean): string => {
  if (isImageMaximized) {
    return (
      image?.extralarge?.default
      || image?.large?.default
      || image?.thumbnail?.default
      || IMAGES.PLACEHOLDERS.GENERIC
    );
  }
  return (
    image?.large?.default
    || image?.thumbnail?.default
    || IMAGES.PLACEHOLDERS.GENERIC
  );
};
